/**
 * Lyvecom widget integration for The Schumann theme.
 *
 * Lyvecom (dashboard.lyvecom.com/widget/widget.js) renders its shoppable
 * carousels / swipers (`.lyvecom-swiper--component`, "Buy Now" tiles) into a
 * SHADOW DOM: a `<div class="lyvecom_carousel-component">` whose `.shadowRoot`
 * holds the product tiles. That shadow boundary is the root cause of the two
 * problems this file fixes.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. BASE-CURRENCY PRICES ON NON-BASE MARKETS
 *    The widget prices its tiles from raw Shopify Admin-API product data —
 *    ALWAYS the shop's base currency (USD) with a hardcoded "$", with no
 *    Shopify Markets awareness. Markets that convert (e.g. Australia: FX rate
 *    plus a +3% price adjustment) showed "$99.99" where the site showed
 *    "$152.00".
 *
 *    Fix: each price node's product (and its variants) lives in the widget's
 *    React fiber (`memoizedProps.product`, Admin JSON with `handle` + base-price
 *    `variants[]`). We fetch the market-aware Ajax product
 *    (`/products/{handle}.js`, cached per handle — it returns presentment-
 *    currency cents for the shopper's market) and render that variant's price
 *    with the market symbol. Surfaces: carousel tiles (`.price`), the mini-PDP
 *    quick-view's class-less main price (in a SEPARATE, lazily-mounted shadow
 *    root, located structurally) and its "You may also like" tiles
 *    (`.product-pricing`). Re-applied on every widget re-render via a
 *    MutationObserver (Swiper clones/loops slides; the popup mounts lazily).
 *
 *    When a node can't be tied to a product with certainty (no fiber product,
 *    a title mismatch, the Ajax call fails), we fall back to swapping only the
 *    currency symbol — never a guessed price.
 *
 * 2. ADD-TO-CART REDIRECTS TO /cart INSTEAD OF OPENING THE DRAWER
 *    The widget's CTA POSTs to /cart/add.js and then runs
 *    `window.location.assign('/cart')` (a Lyvecom dashboard setting). This theme
 *    is drawer-only and /cart is disabled (it redirects to the homepage — see
 *    layout/theme.liquid), so the CTA bounced the shopper off the page.
 *
 *    Fix: intercept the widget's own add request at the fetch layer. We perform
 *    the real add, refresh + open the theme cart drawer, then hand the widget a
 *    non-OK response so its redirect branch is skipped. This is safe because the
 *    widget's add helper is `res.ok ? res.json() : undefined` and the redirect
 *    only runs when that value is truthy — a non-OK response makes it `undefined`
 *    and the `location.assign('/cart')` never fires. The widget's own basket
 *    reset and "addedToCart" analytics event still run normally.
 *
 *    The theme's OWN cart requests are untouched: they ask for the `cart-drawer`
 *    section, whereas Lyvecom asks for `custom-cart-drawer,custom-cart-count` —
 *    that signature is how we tell the widget's add apart from everything else.
 * ─────────────────────────────────────────────────────────────────────────────
 */
(function () {
  "use strict";

  /* ========================================================================
     1. ADD-TO-CART INTERCEPTION
     Installed immediately (this script is deferred, so it runs long before any
     shopper can click a widget CTA). The widget resolves the global `fetch` at
     call time, so wrapping window.fetch is enough to catch its add request.
     ======================================================================== */
  var nativeFetch =
    typeof window.fetch === "function" ? window.fetch.bind(window) : null;

  if (nativeFetch && !window.__schumannLyveFetchPatched) {
    window.__schumannLyveFetchPatched = true;

    window.fetch = function (input, init) {
      try {
        var url = typeof input === "string" ? input : (input && input.url) || "";
        var body = init && typeof init.body === "string" ? init.body : "";
        /* Only Lyvecom asks /cart/add.js for these custom sections. */
        if (/\/cart\/add(\.js)?(\?|$)/.test(url) && /custom-cart/.test(body)) {
          return handleLyveAdd(input, init);
        }
      } catch (e) {
        /* Never let our wrapper break a normal fetch — fall through. */
      }
      return nativeFetch(input, init);
    };
  }

  function handleLyveAdd(input, init) {
    var drawer = document.querySelector("cart-drawer");

    /* No drawer on this page (shouldn't happen — it's site-wide): leave the
       widget's default behavior alone rather than swallow the add silently. */
    if (!drawer) return nativeFetch(input, init);

    return nativeFetch(input, init).then(function (res) {
      if (!res.ok) return res; /* real add failed — let the widget see it */

      /* Real add succeeded server-side. Re-render the drawer from the fresh
         cart (cart:refresh -> the theme's external-cart-update path re-fetches the
         cart-drawer section) and open it. */
      try {
        window.dispatchEvent(new Event("cart:refresh"));
      } catch (e) {}
      try {
        if (typeof drawer.open === "function") drawer.open();
      } catch (e) {}

      /* Hand the widget a non-OK response so its
         `res.ok ? res.json() : undefined` yields undefined and its
         `if (added) location.assign('/cart')` branch is skipped. */
      return new Response("{}", {
        status: 409,
        statusText: "Handled by theme cart drawer",
        headers: { "Content-Type": "application/json" },
      });
    });
  }

  /* ========================================================================
     2. CURRENCY SYMBOL FIX
     ======================================================================== */
  /* ── Currency symbol ────────────────────────────────────────────────────
     The active market's symbol, used both to format market prices and as the
     symbol-only fallback (see note 1 in the file header). ── */
  var CURRENCY_SYMBOLS = {
    USD: "$", CAD: "$", AUD: "$", NZD: "$", HKD: "$", SGD: "$", MXN: "$",
    EUR: "€", GBP: "£", JPY: "¥", CNY: "¥", INR: "₹", CHF: "CHF",
    SEK: "kr", NOK: "kr", DKK: "kr", PLN: "zł", CZK: "Kč", BRL: "R$", ZAR: "R",
  };
  var _marketSymbol = null; /* locked once learned from a theme price node */

  function activeCurrencyCode() {
    try {
      return (
        (window.Shopify &&
          window.Shopify.currency &&
          window.Shopify.currency.active) ||
        null
      );
    } catch (e) {
      return null;
    }
  }

  /* The currency symbol in a rendered price string: "€84.99" -> "€",
     "From $1,299.00" -> "$". Null when the text carries no price. */
  function symbolOf(text) {
    var m = String(text).match(/([^\d\s.,]+)\s*[\d.,]/);
    return m && m[1] ? m[1].trim() : null;
  }

  /* The symbol to render for the active market. Prefer the theme's OWN price
     nodes so we match the store's exact money format (symbol + placement); fall
     back to a currency-code map on pages that render no theme price node. */
  function marketSymbol() {
    if (_marketSymbol) return _marketSymbol;
    var themeNodes = document.querySelectorAll(
      ".rem-ig-price-query, .rem-ig-compare-price-query, .rem-ig-savings-price-query"
    );
    for (var i = 0; i < themeNodes.length; i++) {
      var sym = symbolOf(themeNodes[i].textContent);
      if (sym) return (_marketSymbol = sym); /* cache the ground-truth symbol */
    }
    var code = activeCurrencyCode();
    return (code && CURRENCY_SYMBOLS[code]) || null; /* provisional, uncached */
  }

  /* Replace the currency symbol in a rendered price with the market symbol,
     preserving the number and any surrounding text ("From ", ranges, …). No-op
     on the base market (symbol already matches) and when the market is unknown. */
  function localizeSymbol(text) {
    var sym = marketSymbol();
    if (!sym) return text;
    var current = symbolOf(text);
    if (!current || current === sym) return text;
    return text.split(current).join(sym);
  }

  /* ── Market prices ──────────────────────────────────────────────────── */
  var marketProducts = {}; /* handle -> Promise<Ajax product | null> */
  var written = typeof WeakMap === "function" ? new WeakMap() : null; /* el -> text we wrote */

  function marketProduct(handle) {
    if (!marketProducts[handle]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      marketProducts[handle] = nativeFetch
        ? nativeFetch(root + "products/" + encodeURIComponent(handle) + ".js", {
            headers: { Accept: "application/json" },
          })
            .then(function (res) { return res.ok ? res.json() : null; })
            .catch(function () { return null; })
        : Promise.resolve(null);
    }
    return marketProducts[handle];
  }

  function fiberOf(el) {
    for (var k in el) {
      if (k.indexOf("__reactFiber") === 0 || k.indexOf("__reactInternalInstance") === 0) return el[k];
    }
    return null;
  }

  function sameTitle(a, b) {
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  }

  /* The widget's Admin-JSON product behind a node: walk the React fiber up a
     few hops to the first `memoizedProps.product` with a handle + variants.
     Normalised to { handle, title, variants: [{ id, price }] } (base price). */
  function fiberProduct(el) {
    var f = fiberOf(el);
    for (var hops = 0; f && hops < 12; hops++, f = f.return) {
      var p = f.memoizedProps && f.memoizedProps.product;
      if (p && p.handle && p.variants && p.variants.length) {
        return {
          handle: p.handle,
          title: p.title,
          variants: p.variants.map(function (v) { return { id: Number(v.id), price: v.price }; }),
        };
      }
    }
    return null;
  }

  /* The quick-view's "You may also like" tiles carry no product of their own;
     the list lives higher up as `memoizedProps.productRecommendations`
     (Storefront-GraphQL shape: gid ids, `priceV2.amount`). Pick the entry whose
     title matches the tile's. */
  function recommendedProduct(el, title) {
    if (!title) return null;
    var f = fiberOf(el);
    for (var hops = 0; f && hops < 16; hops++, f = f.return) {
      var recs = f.memoizedProps && f.memoizedProps.productRecommendations;
      if (!recs || !recs.length) continue;
      for (var i = 0; i < recs.length; i++) {
        var r = recs[i];
        var nodes = r && r.variants && r.variants.nodes;
        if (!r || !r.handle || !nodes || !sameTitle(r.title, title)) continue;
        return {
          handle: r.handle,
          title: r.title,
          variants: nodes.map(function (v) {
            return {
              id: Number(String(v.id).split("/").pop()),
              price: v.priceV2 && v.priceV2.amount,
            };
          }),
        };
      }
      return null;
    }
    return null;
  }

  function numberOf(text) {
    var m = String(text).match(/[\d.,]*\d/);
    return m ? parseFloat(m[0].replace(/,/g, "")) : NaN;
  }

  function formatMoney(cents) {
    var sym = marketSymbol() || "$";
    return sym + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* Replace the first "<symbol><number>" in the rendered text, keeping any
     surrounding words ("From ", …). */
  function withPrice(text, cents) {
    return String(text).replace(/[^\d\s.,]*\s*[\d.,]*\d/, formatMoney(cents));
  }

  function writePrice(el, text) {
    if (el.textContent.trim() === text) return;
    el.textContent = text;
    if (written) written.set(el, text);
  }

  /* `resolve(el)` returns the node's product (normalised, see fiberProduct)
     or null, in which case only the currency symbol is swapped. */
  function patchPriceEl(priceEl, resolve) {
    var current = priceEl.textContent.trim();
    if (!current) return;
    if (written && written.get(priceEl) === current) return; /* our own write */

    var product = resolve(priceEl);

    /* Symbol-only now; the market price (when found) overwrites it below. */
    var fallback = localizeSymbol(current);
    if (fallback !== current) writePrice(priceEl, fallback);
    if (!product) return;

    /* The variant the widget is showing: the one whose base price is the number
       on screen. No match means we can't be sure which price this is, so it
       keeps the symbol-only fallback. */
    var shown = numberOf(current);
    var variant = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (parseFloat(product.variants[i].price) === shown) { variant = product.variants[i]; break; }
    }
    if (!variant) return;
    var variantId = variant.id;

    marketProduct(product.handle).then(function (mp) {
      if (!mp || !mp.variants) return;
      var cents = null;
      for (var j = 0; j < mp.variants.length; j++) {
        if (mp.variants[j].id === variantId) { cents = mp.variants[j].price; break; }
      }
      if (typeof cents !== "number") return;
      /* The widget may have re-rendered while we waited; only write over the
         text we planned for (base or symbol-swapped). */
      var now = priceEl.textContent.trim();
      if (now !== current && now !== fallback) return;
      writePrice(priceEl, withPrice(current, cents));
    });
  }

  /* The mini-PDP's MAIN price is a class-less <div> sitting next to the product
     title heading (Lyvecom renders it with no stable class), so `.price` can't
     select it. Locate it structurally: the pure-price leaf inside the title's
     block (the heading's parent), excluding the "You may also like" tiles. */
  function isPurePrice(text) {
    var t = String(text).trim();
    return /\d/.test(t) && /^[^\w\s]?\s?[\d,]+\.?\d*\s*[A-Za-z]{0,3}$/.test(t);
  }

  function pdpMainPriceEls(root) {
    var out = [];
    var details = root.querySelectorAll(".product__details");
    for (var i = 0; i < details.length; i++) {
      var heading = details[i].querySelector("h1, h2, h3, h4");
      var scope = heading && heading.parentElement;
      if (!scope) continue;
      var leaves = scope.querySelectorAll("div, span, p");
      for (var j = 0; j < leaves.length; j++) {
        var el = leaves[j];
        if (
          el.children.length === 0 &&
          !el.closest(".product-description") &&
          isPurePrice(el.textContent)
        ) {
          out.push(el);
        }
      }
    }
    return out;
  }

  function titleNear(el, selector) {
    for (var up = el.parentElement, n = 0; up && n < 5; up = up.parentElement, n++) {
      var h = up.querySelector(selector);
      if (h) return h.textContent;
    }
    return null;
  }

  /* The fiber product, only if its title matches the heading shown with it. */
  function titledFiberProduct(el, title) {
    var p = fiberProduct(el);
    return p && title && sameTitle(p.title, title) ? p : null;
  }

  function patchRoot(root) {
    /* `.price` — carousel / player tiles: the fiber product is the tile's own. */
    var tiles = root.querySelectorAll(".price");
    for (var i = 0; i < tiles.length; i++) patchPriceEl(tiles[i], fiberProduct);

    /* `.product-pricing` — quick-view "You may also like" tiles. */
    var recs = root.querySelectorAll(".product-pricing");
    for (var r = 0; r < recs.length; r++) {
      patchPriceEl(recs[r], function (el) {
        return recommendedProduct(el, titleNear(el, ".product-title"));
      });
    }

    /* The quick-view's class-less main price, checked against its heading. */
    var mains = pdpMainPriceEls(root);
    for (var k = 0; k < mains.length; k++) {
      var details = mains[k].closest(".product__details");
      var heading = details && details.querySelector("h1, h2, h3, h4");
      patchPriceEl(mains[k], function (el) {
        return titledFiberProduct(el, heading && heading.textContent);
      });
    }
  }

  /* ── Discover Lyvecom shadow roots (they mount asynchronously and there can
     be several per page) and wire each one once: patch now + re-patch on any
     re-render. ── */
  var wiredRoots = [];
  var seenRoots =
    typeof WeakSet === "function" ? new WeakSet() : { has: function () { return false; }, add: function () {} };

  function wireRoot(root) {
    if (seenRoots.has(root)) return;
    seenRoots.add(root);
    wiredRoots.push(root);

    patchRoot(root);

    try {
      var scheduled = false;
      var observer = new MutationObserver(function () {
        /* Debounce a burst of mutations into one patch pass; the equality
           guard in patchPriceEl keeps this from looping on our own writes. */
        if (scheduled) return;
        scheduled = true;
        (window.requestAnimationFrame || window.setTimeout)(function () {
          scheduled = false;
          patchRoot(root);
        });
      });
      observer.observe(root, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    } catch (e) {}
  }

  function scan() {
    /* `[class*="lyvecom"]` covers every Lyvecom host, including the carousel
       (.lyvecom_carousel-component) AND the mini-PDP quick-view, which mounts
       into a SEPARATE, lazily-created host (.lyvecom-swiper-selector) the first
       time a shopper opens a product popup. */
    var hosts = document.querySelectorAll('[class*="lyvecom"]');
    for (var i = 0; i < hosts.length; i++) {
      var root = hosts[i].shadowRoot;
      if (root) wireRoot(root);
    }
    /* Re-patch already-wired roots too, so a market symbol learned late (from a
       theme price node) is applied even if the widget hasn't mutated since. */
    for (var j = 0; j < wiredRoots.length; j++) patchRoot(wiredRoots[j]);
  }

  function startBridge() {
    /* Fast discovery for the first ~60s covers widget mount.
       After that we keep polling at a slow cadence — the mini-PDP host can be
       created much later (only when a shopper first opens a popup), and it must
       still get wired. A scan is cheap (a couple of small shadow roots), and the
       per-root MutationObservers handle instant re-patching once wired. */
    var ticks = 0;
    var fast = setInterval(function () {
      scan();
      if (++ticks > 120) {
        clearInterval(fast);
        setInterval(scan, 1500);
      }
    }, 500);
    scan();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startBridge, { once: true });
  } else {
    startBridge();
  }
})();
